/**
 * @file Appointment Detail Page — /cita/:id
 *
 * Two layouts share this route:
 *   - Professional: handoff-13 "Detalle Final" — breadcrumb + pulse-eyebrow
 *     header + timeline (ANTES / LA CITA / DESPUÉS) + patient rail.
 *   - Patient: legacy AppointmentInfoCard layout (pending its own redesign).
 *
 * Cancel + reschedule modals are unchanged across both layouts — they live
 * at the bottom of the component and open from action handlers.
 */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  Video,
  CalendarRange,
  Clock,
  Stethoscope,
  BellRing,
  Phone,
  Calendar as CalIcon,
  Check,
  X,
  ChevronLeft,
  FileText,
  MessageCircle,
} from "lucide-react";
import AppointmentInfoCard from "../components/AppointmentInfoCard";
import { FINAL_STATUSES, ACTIVE_STATUSES } from "../../../shared/appointment-status.js";
import { useAuth } from "../hooks/useAuth";
import { useAppointmentDetail } from "../hooks/useAppointments";
import { appointmentService } from "../services/appointment";
import { waitlistService } from "../services/waitlist";
import { publicService } from "../services/public";
import { APPOINTMENT_STRINGS, COMMON_STRINGS, WAITLIST_STRINGS } from "../constants/strings";
import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogBody,
  DialogFooter,
  Spinner,
  Switch,
  Textarea,
  toast,
} from "../ui";
import "./AppointmentDetail.css";

const formatDateLabel = (dateStr) => {
  const date = new Date(`${dateStr}T12:00:00`);
  return date.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" });
};

const getNextMonths = (n = 6) => {
  const months = [];
  const today = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("es-MX", { month: "long", year: "numeric" });
    months.push({ value, label });
  }
  return months;
};

const NEXT_MONTHS = getNextMonths(6);

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", hour12: false });

const fmtLongDate = (iso) => {
  const d = new Date(iso);
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const wk = cap(d.toLocaleDateString("es-MX", { weekday: "long" }));
  const day = d.getDate();
  const mo = d.toLocaleDateString("es-MX", { month: "short" }).replace(".", "");
  return `${wk} ${day} ${mo}`;
};

const fmtMonthYear = (iso) =>
  new Date(iso).toLocaleDateString("es-MX", { month: "long", year: "numeric" });

const ageFromDob = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
};

const initials = (first = "", last = "") =>
  `${(first || "").charAt(0)}${(last || "").charAt(0)}`.toUpperCase() || "?";

// Pulse-eyebrow text. Future: "En 17 minutos" / "Mañana 09:40" / "Vie 8 may 09:40".
// Past unstarted: "Hace 12 min". Final statuses: their label.
function buildEyebrow(appointment) {
  if (!appointment) return "";
  if (appointment.status === "CANCELADA") return "Cancelada";
  if (appointment.status === "COMPLETADA") return "Completada";
  if (appointment.status === "NO_ASISTIO") return "No asistió";
  const now = new Date();
  const start = new Date(appointment.dateTime);
  const diffMin = Math.round((start.getTime() - now.getTime()) / 60000);
  if (diffMin >= -10 && diffMin <= 60) {
    if (diffMin <= 0) return `En curso · empezó hace ${Math.abs(diffMin)} min`;
    return `En ${diffMin} ${diffMin === 1 ? "minuto" : "minutos"}`;
  }
  if (diffMin > 60 && diffMin < 60 * 24) {
    const h = Math.round(diffMin / 60);
    return `En ${h} ${h === 1 ? "hora" : "horas"}`;
  }
  // Same day cutoff for "Mañana"
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (
    start.getFullYear() === tomorrow.getFullYear() &&
    start.getMonth() === tomorrow.getMonth() &&
    start.getDate() === tomorrow.getDate()
  ) {
    return `Mañana ${fmtTime(appointment.dateTime)}`;
  }
  // Past unstarted (more than 10 min ago, status still active)
  if (diffMin < -10) {
    const abs = Math.abs(diffMin);
    if (abs < 60) return `Hace ${abs} min`;
    if (abs < 60 * 24) return `Hace ${Math.round(abs / 60)} h`;
    return `Hace ${Math.round(abs / 1440)} días`;
  }
  return `${fmtLongDate(appointment.dateTime)} · ${fmtTime(appointment.dateTime)}`;
}

function ProfessionalLayout({
  appointment,
  patient,
  patientUser,
  location,
  service,
  durationMin,
  isFinal,
  canMarkNoShow,
  submitting,
  navigate,
  onComplete,
  onReschedule,
  onCancel,
  onNoShow,
}) {
  const eyebrow = buildEyebrow(appointment);
  const isVirtual = location?.isVirtual === true;
  const modalidad = isVirtual ? "Videoconsulta" : "Presencial";
  const locationLabel = location?.name
    || [location?.address, location?.colonia, location?.city].filter(Boolean).join(", ")
    || "—";

  const showAntes = !!appointment.reminderSentAt
    || appointment.status === "CONFIRMADA"
    || appointment.status === "COMPLETADA";

  const showActions = !isFinal;

  const fullName = [patientUser?.firstName, patientUser?.lastName].filter(Boolean).join(" ").trim()
    || "Paciente";
  const age = ageFromDob(patient?.dateOfBirth);
  const sexLabel = patient?.sex === "F" ? "F" : patient?.sex === "M" ? "M" : null;
  const sub = [age != null ? `${age} años` : null, sexLabel].filter(Boolean).join(" · ");

  const handleContact = () => {
    const phone = patientUser?.phone;
    if (!phone) {
      toast.error("Sin teléfono registrado");
      return;
    }
    const wa = `https://wa.me/${phone.replace(/\D/g, "")}`;
    window.open(wa, "_blank", "noopener");
  };

  const motivo = appointment.reason || appointment.attendeeName
    ? appointment.reason
    : "Sin motivo especificado.";
  const headingMotivo = service?.name || appointment.reason || "consulta";

  return (
    <div className="dc-page-frame">
      <div className="dc-crumb">
        <button type="button" className="dc-back" onClick={() => navigate(-1)}>
          <span className="arrow"><ChevronLeft size={14} /></span>
          {COMMON_STRINGS.BACK || "Volver"}
        </button>
        <div className="right">
          <span className="id">CITA · #{String(appointment.id).padStart(5, "0")}</span>
        </div>
      </div>

      <div className="optB-head">
        <div className="l">
          <span className="when">
            {!isFinal && <span className="pulse" />}
            {eyebrow}
          </span>
          <h1>
            {fullName}, <em>{headingMotivo}</em>
          </h1>
          <div className="motivo">
            {fmtLongDate(appointment.dateTime)}
            {age != null ? ` · ${age} años` : ""}
            {patient?._visitOrdinal ? ` · ${patient._visitOrdinal}` : ""}
          </div>
        </div>
        {showActions && (
          <div className="optB-head-actions">
            <button type="button" className="pg-btn" onClick={onReschedule} disabled={submitting}>
              <CalIcon size={14} /> Reagendar
            </button>
            <button type="button" className="pg-btn" onClick={handleContact}>
              <Phone size={14} /> Contactar
            </button>
          </div>
        )}
      </div>

      <div className="optB-cols final">
        <div className="optB-timeline">
          {showAntes && (
            <div className="optB-step done">
              <div className="ttl">
                Antes de la cita
                {appointment.reminderSentAt && (
                  <span className="when">— {fmtLongDate(appointment.reminderSentAt)}</span>
                )}
              </div>
              {appointment.reminderSentAt && (
                <div className="optB-step-row">
                  <div className="ic wa"><MessageCircle size={16} /></div>
                  <div className="copy">
                    <div className="t">Recordatorio enviado · WhatsApp</div>
                    <div className="s">
                      "Hola {patientUser?.firstName || ""}, te recordamos tu cita…"
                    </div>
                  </div>
                  <div className="meta">
                    <span className="ok">✓</span> ENVIADO {fmtTime(appointment.reminderSentAt)}
                  </div>
                </div>
              )}
              {(appointment.status === "CONFIRMADA" || appointment.status === "COMPLETADA") && (
                <div className="optB-step-row">
                  <div className="ic"><Check size={16} /></div>
                  <div className="copy">
                    <div className="t">Paciente confirmó asistencia</div>
                    <div className="s">Confirmación recibida vía WhatsApp.</div>
                  </div>
                  <div className="meta">
                    <span className="ok">✓</span> CONFIRMADA
                  </div>
                </div>
              )}
            </div>
          )}

          <div className={"optB-step" + (isFinal ? "" : " now")}>
            <div className="ttl">
              La cita
              <span className="when">
                — {fmtLongDate(appointment.dateTime)} · {fmtTime(appointment.dateTime)}
              </span>
            </div>
            <div className="optB-card-now">
              <div className="grid">
                <div className="cell">
                  <div className="l">Hora</div>
                  <div className="v">
                    {fmtTime(appointment.dateTime)}
                    {appointment.endTime && (
                      <span className="light"> – {fmtTime(appointment.endTime)}</span>
                    )}
                  </div>
                </div>
                <div className="cell">
                  <div className="l">Duración</div>
                  <div className="v">{durationMin} min</div>
                </div>
                <div className="cell">
                  <div className="l">Modalidad</div>
                  <div className="v">{modalidad}</div>
                </div>
                <div className="cell">
                  <div className="l">Ubicación</div>
                  <div className="v">{locationLabel}</div>
                </div>
                <div className="cell" style={{ gridColumn: "1 / -1" }}>
                  <div className="l">Motivo de consulta</div>
                  <div className="v" style={{ fontSize: 16 }}>
                    {motivo}
                  </div>
                </div>
              </div>
              {showActions && (
                <div className="actions">
                  {appointment.status === "CONFIRMADA"
                    || appointment.status === "AGENDADA" ? (
                    <button
                      type="button"
                      className="pg-btn cta"
                      onClick={onComplete}
                      disabled={submitting}
                    >
                      <Check size={14} /> Marcar completada
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="pg-btn"
                    onClick={onReschedule}
                    disabled={submitting}
                  >
                    <CalIcon size={14} /> Reagendar
                  </button>
                  {canMarkNoShow && (
                    <button
                      type="button"
                      className="pg-btn"
                      onClick={onNoShow}
                      disabled={submitting}
                    >
                      <X size={14} /> Marcar no-show
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {!isFinal && (
            <div className="optB-step">
              <div className="ttl">Después de la cita</div>
              <div className="optB-followup">
                <div className="ic followup-ic">
                  <CalIcon size={16} />
                </div>
                <div className="copy">
                  <div className="t">Programar seguimiento</div>
                  <div className="s">Crea otra cita con este paciente.</div>
                </div>
                <button
                  type="button"
                  className="pg-btn solid"
                  onClick={() => navigate(`/nueva-cita?patientId=${patient?.id}`)}
                >
                  Agendar
                </button>
              </div>
            </div>
          )}

          {showActions && (
            <div style={{ marginTop: 18, paddingLeft: 28 }}>
              <button type="button" className="dc-btn-link-danger" onClick={onCancel}>
                Cancelar esta cita
              </button>
            </div>
          )}
        </div>

        <aside>
          <div className="final-rail-card">
            <div className="head">
              <div className="av">{initials(patientUser?.firstName, patientUser?.lastName)}</div>
              <div className="info">
                <div className="n">{fullName}</div>
                <div className="s">{sub || "Paciente"}</div>
              </div>
            </div>
            <div className="rail-meta">
              <div className="row">
                <span className="k">Teléfono</span>
                <span className="v">{patientUser?.phone || "—"}</span>
              </div>
              <div className="row">
                <span className="k">Correo</span>
                <span className="v">{patientUser?.email || "—"}</span>
              </div>
              <div className="row">
                <span className="k">Paciente desde</span>
                <span className="v">
                  {patient?.createdAt ? fmtMonthYear(patient.createdAt) : "—"}
                </span>
              </div>
            </div>
            <div className="rail-empty">
              <FileText size={14} />
              <div>
                <strong>Aún sin expediente clínico.</strong>
                Las alergias, padecimientos y medicación se llenarán en la primera consulta.
              </div>
            </div>
          </div>
          <button
            type="button"
            className="pg-btn"
            style={{ width: "100%", justifyContent: "center" }}
            onClick={() => navigate(`/paciente/${patient?.id}`)}
          >
            <FileText size={14} /> Ver ficha del paciente
          </button>
        </aside>
      </div>
    </div>
  );
}

export default function AppointmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { appointment, setAppointment, isLoading } = useAppointmentDetail(parseInt(id));
  const [notes, setNotes] = useState("");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");
  const [shareReason, setShareReason] = useState(false);
  const [notifyWaitlist, setNotifyWaitlist] = useState(true);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [rsLocation, setRsLocation] = useState(null);
  const [rsService, setRsService] = useState(null);
  const [rsMonth, setRsMonth] = useState(null);
  const [rsAvailableDates, setRsAvailableDates] = useState(null);
  const [rsDatesLoading, setRsDatesLoading] = useState(false);
  const [rsDate, setRsDate] = useState(null);
  const [rsSlots, setRsSlots] = useState([]);
  const [rsSlotsLoading, setRsSlotsLoading] = useState(false);
  const [rsSelectedSlot, setRsSelectedSlot] = useState(null);

  const isProfessional = user?.role === "PROFESIONAL";
  const isPatient = user?.role === "PACIENTE";

  useEffect(() => {
    if (isProfessional) {
      waitlistService
        .professionalCount()
        .then((res) => setWaitlistCount(res.data?.count ?? res.data ?? 0))
        .catch(() => {});
    }
  }, [isProfessional]);

  const showWaitlistToggle =
    isProfessional &&
    appointment?.professional?.waitlistEnabled &&
    appointment?.professional?.plan !== "GRATIS" &&
    waitlistCount > 0;

  useEffect(() => {
    if (!rescheduleOpen || !rsMonth || !rsLocation || !appointment?.professional?.slug) return;
    const [year, month] = rsMonth.split("-").map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();
    const today = new Date();
    const isCurrentMonth = year === today.getFullYear() && month === today.getMonth() + 1;
    const dayOffset = appointment.professional.allowSameDayBooking ? 0 : 1;
    const fromDate = isCurrentMonth
      ? new Date(today.getFullYear(), today.getMonth(), today.getDate() + dayOffset)
      : new Date(year, month - 1, 1);
    const from = `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, "0")}-${String(fromDate.getDate()).padStart(2, "0")}`;
    const to = `${rsMonth}-${String(daysInMonth).padStart(2, "0")}`;

    setRsDatesLoading(true);
    setRsAvailableDates(null);
    setRsDate(null);
    setRsSlots([]);
    setRsSelectedSlot(null);

    publicService
      .getAvailableDates(appointment.professional.slug, from, to, rsLocation.id)
      .then((res) => setRsAvailableDates(res.data))
      .catch(() => setRsAvailableDates([]))
      .finally(() => setRsDatesLoading(false));
  }, [rescheduleOpen, rsMonth, rsLocation, appointment?.professional?.slug, appointment?.professional?.allowSameDayBooking]);

  useEffect(() => {
    if (!rescheduleOpen || !rsDate || !rsLocation || !appointment?.professional?.slug) return;
    setRsSlotsLoading(true);
    setRsSlots([]);
    setRsSelectedSlot(null);
    publicService
      .getSlots(appointment.professional.slug, rsDate, rsLocation.id)
      .then((res) => setRsSlots(res.data))
      .catch(() => setRsSlots([]))
      .finally(() => setRsSlotsLoading(false));
  }, [rescheduleOpen, rsDate, rsLocation, appointment?.professional?.slug]);

  const handleOpenReschedule = () => {
    const locations = appointment?.professional?.locations || [];
    const allServices = appointment?.professional?.consultationServices || [];
    const activeServices = allServices.filter((s) => s.isActive);
    setRsLocation(locations.length === 1 ? locations[0] : null);
    setRsService(activeServices.length === 1 ? activeServices[0] : null);
    setRsMonth(null);
    setRsAvailableDates(null);
    setRsDate(null);
    setRsSlots([]);
    setRsSelectedSlot(null);
    setRescheduleOpen(true);
  };

  const handleComplete = async () => {
    try {
      setSubmitting(true);
      const response = await appointmentService.complete(appointment.id);
      toast.success(APPOINTMENT_STRINGS.COMPLETED);
      setAppointment(response.data);
    } catch {
      // ignore
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmAttendance = async () => {
    try {
      setSubmitting(true);
      const response = await appointmentService.confirmAttendance(appointment.id);
      toast.success("¡Asistencia confirmada!");
      setAppointment(response.data);
    } catch {
      // ignore
    } finally {
      setSubmitting(false);
    }
  };

  const canPatientConfirm = (() => {
    if (!isPatient || !appointment || appointment.status !== "AGENDADA") return false;
    const now = new Date();
    const appt = new Date(appointment.dateTime);
    if (appt <= now) return false;
    const toCST = (d) => new Date(d.getTime() - 6 * 60 * 60 * 1000);
    const todayCST = toCST(now).toISOString().slice(0, 10);
    const apptCST = toCST(appt).toISOString().slice(0, 10);
    const tomorrow = new Date(toCST(now));
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowCST = tomorrow.toISOString().slice(0, 10);
    return apptCST === todayCST || apptCST === tomorrowCST;
  })();

  const handleCancel = async () => {
    const trimmed = cancelReason.trim();
    if (!trimmed) {
      setCancelReasonError(APPOINTMENT_STRINGS.CANCEL_REASON_REQUIRED);
      return;
    }
    if (trimmed.length < 10) {
      setCancelReasonError(APPOINTMENT_STRINGS.CANCEL_REASON_MIN);
      return;
    }
    try {
      setSubmitting(true);
      const payload = { cancelReason: trimmed };
      if (isProfessional) payload.shareReason = shareReason;
      if (showWaitlistToggle) payload.notifyWaitlist = notifyWaitlist;
      const response = await appointmentService.cancel(appointment.id, payload);
      toast.success(APPOINTMENT_STRINGS.CANCELLED);
      setAppointment(response.data);
      setCancelDialogOpen(false);
    } catch {
      // ignore
    } finally {
      setSubmitting(false);
    }
  };

  const handleReschedule = async () => {
    try {
      setSubmitting(true);
      const startDt = new Date(rsSelectedSlot.dateTime);
      const duration = appointment.professional.sessionDuration || 60;
      const endDt = new Date(startDt.getTime() + duration * 60000);
      const response = await appointmentService.reschedule(appointment.id, {
        dateTime: startDt.toISOString(),
        endTime: endDt.toISOString(),
        locationId: rsLocation?.id || null,
        serviceId: rsService?.id || null,
      });
      toast.success(APPOINTMENT_STRINGS.RESCHEDULED);
      setAppointment(response.data);
      setRescheduleOpen(false);
    } catch {
      // ignore
    } finally {
      setSubmitting(false);
    }
  };

  const handleNoShow = async () => {
    try {
      setSubmitting(true);
      const response = await appointmentService.noShow(appointment.id);
      toast.success(APPOINTMENT_STRINGS.NO_SHOW_MARKED);
      setAppointment(response.data);
    } catch {
      // ignore
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setSubmitting(true);
      const response = await appointmentService.addNotes(appointment.id, notes);
      toast.success(APPOINTMENT_STRINGS.NOTES_SAVED);
      setAppointment(response.data);
    } catch {
      // ignore
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="apd-page">
        <div className="sk-block sk-h-200" />
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="apd-page">
        <p>{COMMON_STRINGS.NOT_FOUND_APPOINTMENT}</p>
      </div>
    );
  }

  const isFinal = FINAL_STATUSES.includes(appointment.status);
  const now = new Date();
  const appointmentDate = new Date(appointment.dateTime);
  const threeDaysMs = 3 * 24 * 60 * 60 * 1000;
  const canMarkNoShow =
    isProfessional &&
    !isFinal &&
    appointmentDate < now &&
    now - appointmentDate <= threeDaysMs;

  const canReschedule = !!rsSelectedSlot;
  const locations = appointment?.professional?.locations || [];
  const hasMultipleLocations = locations.length > 1;
  const allServices = appointment?.professional?.consultationServices || [];
  const activeServices = allServices.filter((s) => s.isActive);

  const matchedLocation = locations.find((l) => l.id === appointment.locationId) || null;
  const matchedService = allServices.find((s) => s.id === appointment.serviceId) || null;
  const durationMin = Math.max(
    1,
    Math.round(
      (new Date(appointment.endTime).getTime() - new Date(appointment.dateTime).getTime()) / 60000
    )
  );

  return (
    <>
      {isProfessional ? (
        <ProfessionalLayout
          appointment={appointment}
          patient={appointment.patient}
          patientUser={appointment.patient?.user}
          location={matchedLocation}
          service={matchedService}
          durationMin={durationMin}
          isFinal={isFinal}
          canMarkNoShow={canMarkNoShow}
          submitting={submitting}
          navigate={navigate}
          onComplete={handleComplete}
          onReschedule={handleOpenReschedule}
          onCancel={() => setCancelDialogOpen(true)}
          onNoShow={handleNoShow}
        />
      ) : (
        <div className="apd-page">
          <Button variant="ghost" leftIcon={<ArrowLeft size={14} />} onClick={() => navigate(-1)}>
            {COMMON_STRINGS.BACK}
          </Button>

          <Card>
            <AppointmentInfoCard
              appointment={appointment}
              title={APPOINTMENT_STRINGS.DETAIL_TITLE}
              showProfessional={!isProfessional}
              showPatient
              showEndTime={isProfessional}
              showPatientContact={isProfessional}
              showNotes
            />

            {!isFinal && (
              <div className="apd-actions">
                {canPatientConfirm && (
                  <Button onClick={handleConfirmAttendance} loading={submitting}>
                    Confirmar asistencia
                  </Button>
                )}
                <Button variant="outline" onClick={handleOpenReschedule} disabled={submitting}>
                  {APPOINTMENT_STRINGS.RESCHEDULE}
                </Button>
                <Button
                  variant="danger"
                  onClick={() => setCancelDialogOpen(true)}
                  disabled={submitting}
                >
                  {APPOINTMENT_STRINGS.CANCEL}
                </Button>
              </div>
            )}
          </Card>
        </div>
      )}

      {isProfessional && (
        <div className="apd-pro-notes-host">
          {/* Notes textarea kept available in pro detail; renders below the
              dc-page-frame so the visual of the new layout stays clean. */}
          {!isFinal && (
            <div className="apd-pro-notes">
              <div className="apd-pro-notes-inner">
                <h3 className="apd-section-title">{APPOINTMENT_STRINGS.NOTES}</h3>
                <Textarea
                  rows={4}
                  placeholder={APPOINTMENT_STRINGS.NOTES_PLACEHOLDER}
                  value={notes || appointment.notes || ""}
                  onChange={(e) => setNotes(e.target.value)}
                />
                <Button onClick={handleSaveNotes} loading={submitting} style={{ marginTop: 10 }}>
                  {APPOINTMENT_STRINGS.SAVE_NOTES}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog
        open={cancelDialogOpen}
        onOpenChange={(v) => {
          setCancelDialogOpen(v);
          if (!v) {
            setCancelReasonError("");
            setShareReason(false);
          }
        }}
      >
        <DialogContent size="sm" title={APPOINTMENT_STRINGS.CANCEL}>
          <DialogBody>
            <Textarea
              autoFocus
              rows={3}
              label={APPOINTMENT_STRINGS.CANCEL_REASON}
              value={cancelReason}
              onChange={(e) => {
                setCancelReason(e.target.value);
                setCancelReasonError("");
              }}
              error={cancelReasonError}
            />
            {isProfessional && (
              <div className="apd-waitlist" style={{ marginTop: 12 }}>
                <div className="apd-waitlist-text">
                  <strong>Compartir motivo con el paciente</strong>
                </div>
                <Switch
                  checked={shareReason}
                  onCheckedChange={(c) => setShareReason(!!c)}
                />
              </div>
            )}
            {showWaitlistToggle && (
              <div className="apd-waitlist">
                <BellRing size={18} />
                <div className="apd-waitlist-text">
                  <strong>{WAITLIST_STRINGS.NOTIFY_WAITLIST_QUESTION}</strong>
                </div>
                <Switch
                  checked={notifyWaitlist}
                  onCheckedChange={(c) => setNotifyWaitlist(!!c)}
                />
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelDialogOpen(false)}>
              Volver
            </Button>
            <Button variant="danger" onClick={handleCancel} loading={submitting}>
              {APPOINTMENT_STRINGS.CANCEL}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent size="md" title={APPOINTMENT_STRINGS.RESCHEDULE}>
          <DialogBody>
            <div className="apd-rs">
              {hasMultipleLocations && (
                <section className="apd-rs-section">
                  <h4 className="apd-rs-title"><MapPin size={14} /> Ubicación</h4>
                  <div className="apd-rs-options">
                    {locations.map((loc) => (
                      <button
                        key={loc.id}
                        type="button"
                        className={`apd-rs-card ${rsLocation?.id === loc.id ? "apd-rs-card-on" : ""}`}
                        onClick={() => {
                          setRsLocation(loc);
                          setRsService(null);
                          setRsMonth(null);
                          setRsAvailableDates(null);
                          setRsDate(null);
                          setRsSlots([]);
                          setRsSelectedSlot(null);
                          if (activeServices.length === 1) setRsService(activeServices[0]);
                        }}
                      >
                        <div className="apd-rs-card-head">
                          {loc.isVirtual ? <Video size={14} /> : <MapPin size={14} />}
                          <strong>{loc.name || loc.address}</strong>
                        </div>
                        {!loc.isVirtual && (loc.address || loc.colonia || loc.city) && (
                          <span className="apd-rs-card-sub">
                            {[loc.address, loc.colonia, loc.city, loc.state].filter(Boolean).join(", ")}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {!hasMultipleLocations && locations.length === 1 && (
                <div className="apd-rs-info">
                  {locations[0].isVirtual ? <Video size={14} /> : <MapPin size={14} />}
                  <span>
                    {locations[0].isVirtual
                      ? locations[0].name || "Teleconsulta"
                      : [locations[0].name, locations[0].address, locations[0].colonia, locations[0].city]
                          .filter(Boolean)
                          .join(", ")}
                  </span>
                </div>
              )}

              {rsLocation && activeServices.length > 1 && (
                <section className="apd-rs-section">
                  <h4 className="apd-rs-title"><Stethoscope size={14} /> Selecciona un servicio</h4>
                  <div className="apd-rs-options">
                    {activeServices.map((svc) => (
                      <button
                        key={svc.id}
                        type="button"
                        className={`apd-rs-card ${rsService?.id === svc.id ? "apd-rs-card-on" : ""}`}
                        onClick={() => {
                          setRsService(svc);
                          setRsMonth(null);
                          setRsAvailableDates(null);
                          setRsDate(null);
                          setRsSlots([]);
                          setRsSelectedSlot(null);
                        }}
                      >
                        <div className="apd-rs-card-head">
                          <strong>{svc.name}</strong>
                          {svc.price != null && (
                            <span className="apd-rs-card-price">
                              ${svc.price.toLocaleString("es-MX")}{" "}
                              {appointment?.professional?.currency || "MXN"}
                            </span>
                          )}
                        </div>
                        {svc.description && (
                          <span className="apd-rs-card-sub">{svc.description}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {rsLocation && activeServices.length === 1 && (
                <div className="apd-rs-info">
                  <Stethoscope size={14} />
                  <span>
                    {activeServices[0].name}
                    {activeServices[0].price != null &&
                      ` ($${activeServices[0].price.toLocaleString("es-MX")} ${
                        appointment?.professional?.currency || "MXN"
                      })`}
                  </span>
                </div>
              )}

              {rsLocation && rsService && (
                <section className="apd-rs-section">
                  <h4 className="apd-rs-title"><CalendarRange size={14} /> Selecciona un mes</h4>
                  <div className="apd-rs-pills">
                    {NEXT_MONTHS.map((m) => (
                      <button
                        key={m.value}
                        type="button"
                        className={`apd-rs-pill ${rsMonth === m.value ? "apd-rs-pill-on" : ""}`}
                        onClick={() => {
                          setRsMonth(m.value);
                          setRsDate(null);
                          setRsSelectedSlot(null);
                        }}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {rsMonth && (
                <section className="apd-rs-section">
                  <h4 className="apd-rs-title">Selecciona un día</h4>
                  {rsDatesLoading ? (
                    <Spinner size={20} />
                  ) : !rsAvailableDates || rsAvailableDates.length === 0 ? (
                    <p className="apd-rs-empty">Sin disponibilidad este mes en esta ubicación.</p>
                  ) : (
                    <div className="apd-rs-pills">
                      {rsAvailableDates.map((date) => (
                        <button
                          key={date}
                          type="button"
                          className={`apd-rs-pill ${rsDate === date ? "apd-rs-pill-on" : ""}`}
                          onClick={() => {
                            setRsDate(date);
                            setRsSelectedSlot(null);
                          }}
                        >
                          {formatDateLabel(date)}
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              )}

              {rsDate && (
                <section className="apd-rs-section">
                  <h4 className="apd-rs-title"><Clock size={14} /> Selecciona un horario</h4>
                  {rsSlotsLoading ? (
                    <Spinner size={20} />
                  ) : rsSlots.length === 0 ? (
                    <p className="apd-rs-empty">Sin horarios disponibles.</p>
                  ) : (
                    <div className="apd-rs-pills">
                      {rsSlots.map((slot) => (
                        <button
                          key={slot.startTime}
                          type="button"
                          className={`apd-rs-pill ${rsSelectedSlot?.startTime === slot.startTime ? "apd-rs-pill-on" : ""}`}
                          onClick={() => setRsSelectedSlot(slot)}
                        >
                          {slot.startTime}
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRescheduleOpen(false)}>
              Volver
            </Button>
            <Button onClick={handleReschedule} disabled={!canReschedule} loading={submitting}>
              {APPOINTMENT_STRINGS.RESCHEDULE}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
